import React, { useEffect, useState } from 'react';
import { FiSearch, FiTag, FiX } from 'react-icons/fi';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../SEO/SEO';
import BlogCard from './BlogCard';
import { blogsAPI } from '../../services/blogApi';
import { BlogListItem } from '../../types';

const BlogList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag')?.trim() || '';

  const [blogs, setBlogs] = useState<BlogListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const controller = new AbortController();

    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await blogsAPI.list(
          page,
          9,
          search || undefined,
          activeTag || undefined
        );
        setBlogs(data.data);
        setTotalPages(data.total_pages);
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load blogs');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchBlogs();
    return () => controller.abort();
  }, [page, search, activeTag]);

  const clearTagFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('tag');
    setSearchParams(next);
    setPage(1);
  };

  return (
    <>
      <SEO
        title={
          activeTag
            ? `${activeTag} - Travel Blog`
            : 'Travel Blog - Tips, Guides & Visa Information'
        }
        description="Read travel guides, visa tips, passport photo advice and destination insights from travelbaba786."
        keywords="travel blog, visa guide, travel tips, passport photo guide, destination travel"
        url={activeTag ? `/blog?tag=${encodeURIComponent(activeTag)}` : '/blog'}
      />

      <div className="max-w-6xl mx-auto py-10 px-4">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Travel Blog
          </h1>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Expert travel guides, visa tips, and passport photo advice to help you plan your next journey.
          </p>
        </div>

        {activeTag && (
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-sm font-semibold border border-blue-200 dark:border-blue-800">
              <FiTag size={14} />
              Tag: {activeTag}
            </span>
            <button
              type="button"
              onClick={clearTagFilter}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              <FiX size={14} /> Clear filter
            </button>
          </div>
        )}

        <div className="relative max-w-xl mx-auto mb-10">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search articles..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
          />
        </div>

        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-80 rounded-2xl bg-gray-200 dark:bg-slate-700 animate-pulse" />
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-16 text-red-500">{error}</div>
        )}

        {!loading && !error && blogs.length === 0 && (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            {activeTag ? (
              <>
                No articles found for tag &quot;{activeTag}&quot;.{' '}
                <Link to="/blog" className="text-blue-600 font-semibold hover:underline">
                  View all articles
                </Link>
              </>
            ) : (
              'No articles published yet. Check back soon!'
            )}
          </div>
        )}

        {!loading && !error && blogs.length > 0 && (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogs.map((blog) => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center gap-3 mt-10">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-gray-600 dark:text-gray-300">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default BlogList;
